/**
 * Product Wizard
 * Handles wizard navigation, answer collection, and product matching
 */

const productWizard = () => ({
  // State management
  wizardData: [],
  currentScreen: 'question',
  currentQuestionIndex: 0,
  selectedAnswers: {},
  recommendedProducts: [],
  recommendedProductsByCollection: [],
  isLoading: false,
  isAddingToCart: false,
  totalSteps: 0, // Total questions + results step
  shopCurrency: 'USD',
  shopLocale: 'en-US',

  // Computed properties
  get currentQuestion() {
    return this.wizardData[this.currentQuestionIndex] || null;
  },

  get isLastQuestion() {
    return this.currentQuestionIndex === this.wizardData.length - 1;
  },

  get canProceed() {
    return this.canProceedFromQuestion();
  },

  // Initialization
  init() {
    // Get data from the element's data attributes
    this.wizardData = JSON.parse(this.$el.dataset.wizardData || '[]');
    
    // Get currency and locale information
    this.shopCurrency = this.$el.dataset.shopCurrency || 'USD';
    this.shopLocale = this.$el.dataset.shopLocale || 'en-US';
    
    // Calculate total steps (questions + results step)
    this.totalSteps = this.wizardData.length + 1;
    
    console.log('Product Wizard initialized with data:', this.wizardData);
    console.log('Shop currency:', this.shopCurrency);
    console.log('Shop locale:', this.shopLocale);
    console.log('Total steps (including results):', this.totalSteps);
    
    // Validate wizard data
    if (!this.wizardData || this.wizardData.length === 0) {
      console.warn('No wizard questions configured');
      return;
    }
  },
  // Progress tracking methods
  get currentStepIndex() {
    if (this.currentScreen === 'results') {
      return this.wizardData.length; // Results step is after all questions
    }
    return this.currentQuestionIndex;
  },

  get isOnResultsStep() {
    return this.currentScreen === 'results';
  },

  isStepCompleted(stepIndex) {
    if (stepIndex < this.wizardData.length) {
      // This is a question step
      return this.isQuestionCompleted(stepIndex);
    } else {
      // This is the results step
      return this.currentScreen === 'results' && this.recommendedProducts.length > 0;
    }
  },

  isStepCurrent(stepIndex) {
    if (stepIndex < this.wizardData.length) {
      // This is a question step
      return this.currentScreen === 'question' && this.currentQuestionIndex === stepIndex;
    } else {
      // This is the results step
      return this.currentScreen === 'results';
    }
  },

  canNavigateToStep(stepIndex) {
    if (stepIndex < this.wizardData.length) {
      // Can navigate to question steps that are at or before current progress
      return stepIndex <= this.currentQuestionIndex;
    } else {
      // Can only navigate to results step if all questions are completed
      return this.areAllQuestionsCompleted();
    }
  },

  areAllQuestionsCompleted() {
    for (let i = 0; i < this.wizardData.length; i++) {
      if (!this.isQuestionCompleted(i)) {
        return false;
      }
    }
    return true;
  },

  navigateToStep(stepIndex) {
    if (!this.canNavigateToStep(stepIndex)) {
      return;
    }

    if (stepIndex < this.wizardData.length) {
      // Navigate to question step
      this.currentScreen = 'question';
      this.currentQuestionIndex = stepIndex;
    } else {
      // Navigate to results step (only if all questions completed)
      if (this.areAllQuestionsCompleted()) {
        this.processResults();
      }
    }
  },

  // Navigation methods
  startWizard() {
    this.currentScreen = 'question';
    this.currentQuestionIndex = 0;
    this.selectedAnswers = {};
    this.recommendedProducts = [];
  },

  selectAnswer(answerIndex) {
    const currentQuestion = this.currentQuestion;
    if (!currentQuestion) return;

    if (currentQuestion.type === 'multi') {
      this.selectAnswerMulti(answerIndex);
    } else {
      this.selectAnswerSingle(answerIndex);
    }
  },

  selectAnswerSingle(answerIndex) {
    this.selectedAnswers[this.currentQuestionIndex] = answerIndex;
    console.log('Single answer selected:', {
      question: this.currentQuestionIndex,
      answer: answerIndex,
      answerData: this.currentQuestion.answers[answerIndex]
    });
  },

  selectAnswerMulti(answerIndex) {
    if (!this.selectedAnswers[this.currentQuestionIndex]) {
      this.selectedAnswers[this.currentQuestionIndex] = [];
    }

    const currentSelections = this.selectedAnswers[this.currentQuestionIndex];
    const existingIndex = currentSelections.indexOf(answerIndex);

    if (existingIndex > -1) {
      // Remove if already selected
      currentSelections.splice(existingIndex, 1);
    } else {
      // Add if not selected
      currentSelections.push(answerIndex);
    }

    console.log('Multi answer toggled:', {
      question: this.currentQuestionIndex,
      answer: answerIndex,
      currentSelections: currentSelections,
      answerData: this.currentQuestion.answers[answerIndex]
    });
  },

  isAnswerSelected(answerIndex) {
    const currentQuestion = this.currentQuestion;
    if (!currentQuestion) return false;

    const selections = this.selectedAnswers[this.currentQuestionIndex];
    
    if (currentQuestion.type === 'multi') {
      return Array.isArray(selections) && selections.includes(answerIndex);
    } else {
      return selections === answerIndex;
    }
  },

  canProceedFromQuestion() {
    const currentQuestion = this.currentQuestion;
    if (!currentQuestion) return false;

    const selections = this.selectedAnswers[this.currentQuestionIndex];

    if (currentQuestion.type === 'multi') {
      return Array.isArray(selections) && selections.length > 0;
    } else {
      return selections !== undefined;
    }
  },

  isQuestionCompleted(questionIndex) {
    const question = this.wizardData[questionIndex];
    if (!question) return false;

    const selections = this.selectedAnswers[questionIndex];

    if (question.type === 'multi') {
      return Array.isArray(selections) && selections.length > 0;
    } else {
      return selections !== undefined;
    }
  },

  navigateToQuestion(questionIndex) {
    // Only allow backward navigation or to current question
    if (questionIndex <= this.currentQuestionIndex) {
      this.currentQuestionIndex = questionIndex;
    }
  },

  getDefaultInstruction() {
    const currentQuestion = this.currentQuestion;
    if (!currentQuestion) return '';

    if (currentQuestion.instruction && currentQuestion.instruction.trim()) {
      return currentQuestion.instruction;
    }

    return currentQuestion.type === 'multi' 
      ? 'CHOOSE AS MANY ANSWERS AS YOU LIKE'
      : 'CHOOSE ONE ANSWER';
  },

  nextQuestion() {
    if (!this.canProceed) {
      console.warn('Cannot proceed without selecting an answer');
      return;
    }

    if (this.isLastQuestion) {
      this.processResults();
    } else {
      this.currentQuestionIndex++;
    }
  },

  previousQuestion() {
    if (this.currentQuestionIndex > 0) {
      this.currentQuestionIndex--;
    }
  },

  restartWizard() {
    this.currentScreen = 'question';
    this.currentQuestionIndex = 0;
    this.selectedAnswers = {};
    this.recommendedProducts = [];
    this.recommendedProductsByCollection = [];
  },

  // Cart functionality
  async addAllToCart() {
    if (!this.recommendedProducts || this.recommendedProducts.length === 0) {
      console.warn('No products to add to cart');
      return;
    }

    this.isAddingToCart = true;

    try {
      // Prepare items for cart
      const cartItems = [];
      const unavailableProducts = [];

      for (const product of this.recommendedProducts) {
        // Get the first available variant
        const availableVariant = this.getFirstAvailableVariant(product);
        
        if (availableVariant) {
          cartItems.push({
            id: availableVariant.id,
            quantity: 1
          });
        } else {
          unavailableProducts.push(product.title);
        }
      }

      if (cartItems.length === 0) {
        this.showCartMessage('All recommended products are currently unavailable.', 'error');
        return;
      }

      // Add items to cart using Shopify's Cart API
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ items: cartItems })
      });

      if (!response.ok) {
        throw new Error(`Cart API error: ${response.status}`);
      }

      const result = await response.json();
      console.log('Items added to cart:', result);

      // Update cart UI elements
      this.updateCartUI();

      // Show success message
      let message = `Successfully added ${cartItems.length} product${cartItems.length > 1 ? 's' : ''} to your cart!`;
      
      if (unavailableProducts.length > 0) {
        message += ` Note: ${unavailableProducts.length} product${unavailableProducts.length > 1 ? 's were' : ' was'} unavailable and skipped.`;
      }

      this.showCartMessage(message, 'success');

    } catch (error) {
      console.error('Error adding products to cart:', error);
      this.showCartMessage('There was an error adding products to your cart. Please try again.', 'error');
    } finally {
      this.isAddingToCart = false;
    }
  },

  async addSingleProductToCart(product) {
    if (!product) {
      console.warn('No product provided to add to cart');
      return;
    }

    this.isAddingToCart = true;

    try {
      // Get the first available variant
      const availableVariant = this.getFirstAvailableVariant(product);
      
      if (!availableVariant) {
        this.showCartMessage(`${product.title} is currently unavailable.`, 'error');
        return;
      }

      // Add single item to cart using Shopify's Cart API
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          items: [{
            id: availableVariant.id,
            quantity: 1
          }]
        })
      });

      if (!response.ok) {
        throw new Error(`Cart API error: ${response.status}`);
      }

      const result = await response.json();
      console.log('Item added to cart:', result);

      // Update cart UI elements
      this.updateCartUI();

      // Show success message
      this.showCartMessage(`${product.title} added to your cart!`, 'success');

    } catch (error) {
      console.error('Error adding product to cart:', error);
      this.showCartMessage(`There was an error adding ${product.title} to your cart. Please try again.`, 'error');
    } finally {
      this.isAddingToCart = false;
    }
  },

  getFirstAvailableVariant(product) {
    if (!product || !product.variants || product.variants.length === 0) {
      return null;
    }

    // Find the first available variant
    return product.variants.find(variant => variant.available) || null;
  },

  updateCartUI() {
    // Trigger cart update events to refresh cart drawer, cart icon, etc.
    if (window.publish && window.PUB_SUB_EVENTS) {
      window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'product-wizard' });
    }

    // Also dispatch a custom event for any other cart listeners
    document.dispatchEvent(new CustomEvent('cart:updated', {
      detail: { source: 'product-wizard' }
    }));
  },

  showCartMessage(message, type = 'success') {
    // Create and show a temporary message
    const messageElement = document.createElement('div');
    messageElement.className = `c-product-wizard__cart-message c-product-wizard__cart-message--${type}`;
    messageElement.textContent = message;
    messageElement.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: ${type === 'success' ? '#4CAF50' : '#f44336'};
      color: white;
      padding: 12px 20px;
      border-radius: 4px;
      z-index: 10000;
      max-width: 300px;
      box-shadow: 0 2px 10px rgba(0,0,0,0.1);
      font-size: 14px;
      line-height: 1.4;
    `;

    document.body.appendChild(messageElement);

    // Remove message after 5 seconds
    setTimeout(() => {
      if (messageElement.parentNode) {
        messageElement.parentNode.removeChild(messageElement);
      }
    }, 5000);
  },

  // Product matching logic
  async processResults() {
    this.currentScreen = 'loading';
    this.isLoading = true;

    try {
      // Collect selected tags from answers
      const selectedTags = this.collectSelectedTags();
      console.log('Selected tags for matching:', selectedTags);

      // Find matching products
      const matchedProducts = await this.findBestMatchingProducts(selectedTags);
      
      this.recommendedProducts = matchedProducts;
      this.recommendedProductsByCollection = this.organizeProductsByCollections(matchedProducts);
      this.currentScreen = 'results';
      
      console.log('Recommended products:', matchedProducts);
      console.log('Products organized by collection:', this.recommendedProductsByCollection);
    } catch (error) {
      console.error('Error processing results:', error);
      // Show latest products on error
      this.recommendedProducts = await this.getLatestProducts();
      this.recommendedProductsByCollection = this.organizeProductsByCollections(this.recommendedProducts);
      this.currentScreen = 'results';
    } finally {
      this.isLoading = false;
    }
  },

  collectSelectedTags() {
    const tags = [];
    
    Object.keys(this.selectedAnswers).forEach(questionIndex => {
      const selections = this.selectedAnswers[questionIndex];
      const question = this.wizardData[questionIndex];
      
      if (!question || !question.answers) return;

      if (question.type === 'multi' && Array.isArray(selections)) {
        // Handle multiple selections
        selections.forEach(answerIndex => {
          const answer = question.answers[answerIndex];
          if (answer && answer.tag && answer.tag.trim()) {
            tags.push(answer.tag.trim().toLowerCase());
          }
        });
      } else if (question.type === 'single' && selections !== undefined) {
        // Handle single selection
        const answer = question.answers[selections];
        if (answer && answer.tag && answer.tag.trim()) {
          tags.push(answer.tag.trim().toLowerCase());
        }
      }
    });

    return tags;
  },

  async findBestMatchingProducts(selectedTags) {
    try {
      // Fetch all products from Shopify
      const products = await this.fetchAllProducts();
      
      if (!products || products.length === 0) {
        console.log('No products found, returning empty array');
        return [];
      }

      if (!selectedTags || selectedTags.length === 0) {
        console.log('No tags selected, returning latest products');
        return this.getLatestProductsFromList(products, 12);
      }

      // Score products based on tag matches
      const scoredProducts = products.map(product => {
        const productTags = (product.tags || []).map(tag => tag.toLowerCase());
        const matchCount = selectedTags.filter(tag => productTags.includes(tag)).length;
        
        return {
          product,
          score: matchCount,
          matchedTags: selectedTags.filter(tag => productTags.includes(tag))
        };
      });

      // Sort by score (highest first), then by creation date (newest first)
      scoredProducts.sort((a, b) => {
        if (b.score !== a.score) {
          return b.score - a.score;
        }
        // If scores are equal, sort by creation date
        return new Date(b.product.created_at) - new Date(a.product.created_at);
      });

      // Get up to 12 products with matching tags, or fall back to latest products
      const matchingProducts = scoredProducts.filter(item => item.score > 0);
      
      if (matchingProducts.length > 0) {
        const selectedProducts = matchingProducts.slice(0, 12).map(item => item.product);
        console.log('Found matching products:', selectedProducts.length);
        return selectedProducts;
      } else {
        console.log('No matching products found, returning latest products');
        return this.getLatestProductsFromList(products, 12);
      }
    } catch (error) {
      console.error('Error finding matching products:', error);
      return await this.getLatestProducts();
    }
  },

  getLatestProductsFromList(products, limit = 12) {
    // Sort products by creation date (newest first)
    const sortedProducts = products.sort((a, b) => {
      return new Date(b.created_at) - new Date(a.created_at);
    });
    
    return sortedProducts.slice(0, limit);
  },

  async getLatestProducts(limit = 12) {
    try {
      const products = await this.fetchAllProducts();
      if (!products || products.length === 0) {
        return [];
      }
      
      return this.getLatestProductsFromList(products, limit);
    } catch (error) {
      console.error('Error fetching latest products:', error);
      return [];
    }
  },

  // Organize products by collections
  organizeProductsByCollections(products) {
    if (!products || products.length === 0) {
      return [];
    }

    // Create a map to store collections and their products
    const collectionsMap = new Map();

    // Process each product and add it to relevant collections
    products.forEach(product => {
      if (product.collections && product.collections.length > 0) {
        // Product belongs to collections - add to each collection
        product.collections.forEach(collection => {
          if (!collectionsMap.has(collection.id)) {
            collectionsMap.set(collection.id, {
              collection: {
                id: collection.id,
                title: collection.title,
                handle: collection.handle
              },
              products: []
            });
          }
          collectionsMap.get(collection.id).products.push(product);
        });
      } else {
        // Product doesn't belong to any collection - add to "Other Products"
        const otherKey = 'other-products';
        if (!collectionsMap.has(otherKey)) {
          collectionsMap.set(otherKey, {
            collection: {
              id: otherKey,
              title: 'Recommended Products',
              handle: 'recommended-products'
            },
            products: []
          });
        }
        collectionsMap.get(otherKey).products.push(product);
      }
    });

    // Convert map to array and sort collections alphabetically
    const collectionGroups = Array.from(collectionsMap.values());
    
    // Sort collections alphabetically, but put "Other Products" last
    collectionGroups.sort((a, b) => {
      if (a.collection.id === 'other-products') return 1;
      if (b.collection.id === 'other-products') return -1;
      return a.collection.title.localeCompare(b.collection.title);
    });

    console.log('Organized products by collections:', collectionGroups);
    return collectionGroups;
  },

  async fetchAllProducts() {
    try {
      // Use Shopify's AJAX API to fetch products
      const response = await fetch('/products.json?limit=12');
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      const products = data.products || [];
      
      console.log( products );

      // Process products to ensure proper price structure
      const processedProducts = products.map(product => {
        // Get the first available variant or first variant
        const firstVariant = product.variants && product.variants.length > 0 ? product.variants[0] : null;
        
        if (firstVariant) {
          // Add price information from the first variant
          product.price = firstVariant.price;
          product.compare_at_price = firstVariant.compare_at_price;
          product.available = firstVariant.available;
        }
        
        return product;
      });
      
      console.log('Processed products with price data:', processedProducts.slice(0, 2)); // Log first 2 products for debugging
      return processedProducts;
    } catch (error) {
      console.error('Error fetching products:', error);
      return [];
    }
  },


  // Utility methods
  formatPrice(price) {
    // Handle different price formats
    if (price === null || price === undefined || price === '') {
      return '';
    }
    
    let numericPrice;
    
    // Convert price to number if it's a string
    if (typeof price === 'string') {
      numericPrice = parseFloat(price);
    } else if (typeof price === 'number') {
      numericPrice = price;
    } else {
      return '';
    }
    
    // Check if the price is valid
    if (isNaN(numericPrice) || numericPrice < 0) {
      return '';
    }
    
    // Shopify API returns prices in cents for some endpoints, dollars for others
    // If the price is a large number (>1000), it's likely in cents
    // If it's a small number (<100), it's likely in dollars
    let amount;
    if (numericPrice > 1000) {
      // Likely in cents, convert to dollars
      amount = numericPrice / 100;
    } else {
      // Likely already in dollars
      amount = numericPrice;
    }
    
    try {
      // Use the shop's currency and locale for formatting
      return new Intl.NumberFormat(this.shopLocale, {
        style: 'currency',
        currency: this.shopCurrency
      }).format(amount);
    } catch (error) {
      console.warn('Error formatting price with locale/currency:', {
        locale: this.shopLocale,
        currency: this.shopCurrency,
        price: price,
        amount: amount,
        error: error.message
      });
      
      // Fallback to USD formatting if there's an error
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(amount);
    }
  },

  getProductImageUrl(product, size = 'medium') {
    if (!product || !product.images || product.images.length === 0) {
      return '';
    }

    const image = product.images[0];
    if (typeof image === 'string') {
      return image;
    }

    return image.src || image.url || '';
  },

  getProductUrl(product) {
    if (!product || !product.handle) {
      return '#';
    }
    return `/products/${product.handle}`;
  }
});

document.addEventListener('alpine:init', () => {
  Alpine.data('productWizard', productWizard);
});
